import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { OptionsList } from 'gung-common';
import { Observable, catchError, from, map, mergeMap, of, shareReplay, switchMap, tap, toArray } from 'rxjs';
import {
  ActivityInternal,
  ActivityInternalService
} from '../../../services/activity-internal/activity-internal.service';
import { GungModalService } from '../../../services/gung-modal/gung-modal.service';
import { GungStringConverterService } from '../../../services/gung-string-converter.service';
import { MetadataService } from '../../../services/metadata/metadata.service';
import { GetMetadata } from '../../../state/metadata/actions';
import { SalesInsightsActivityType } from '../models/sales-insights.model';
import {
  salesInsightsMovedActivityDates,
  salesInsightsReadableName,
  salesInsightsSavedActivityIds,
  salesInsightsTypeColorIndex,
  salesInsightsTypeIcon
} from '../utils/sales-insights-activities';
import {
  salesInsightsInactiveTypes,
  salesInsightsTypeColors,
  salesInsightsTypeIcons
} from '../utils/sales-insights-activity-types';
import { SalesInsightsService } from './sales-insights.service';

export const SALES_INSIGHTS_ACTIVITY_STATUS_TABLE = 'ACTIVITYINTERNALSTATUS';
export const SALES_INSIGHTS_ACTIVITY_TYPE_TABLE = 'ACTIVITYINTERNALTYPE';

// The activity view modal marks an activity closed with the status named COMPLETED; the same status counts as done here.
const COMPLETED_STATUS_NAME = 'COMPLETED';
const WRITE_CONCURRENCY = 4;

export interface SalesInsightsBulkResult {
  saved: number;
  failed: number;
}

@Injectable({
  providedIn: 'root'
})
export class SalesInsightsActivityService {
  protected activityInternalService = inject(ActivityInternalService);
  protected metadataService = inject(MetadataService);
  protected gungModalService = inject(GungModalService);
  protected router = inject(Router);
  protected salesInsightsService = inject(SalesInsightsService);
  protected store = inject(Store);

  private typesCache: Observable<SalesInsightsActivityType[]> | null = null;
  private types: SalesInsightsActivityType[] = [];

  // The type settings every activity component waits for once, so colours and icons are right on the first render.
  loadTypes(): Observable<SalesInsightsActivityType[]> {
    if (!this.typesCache) {
      this.typesCache = this.salesInsightsService.getActivityTypes().pipe(
        catchError(() => of([] as SalesInsightsActivityType[])),
        tap(types => (this.types = types || [])),
        shareReplay(1)
      );
    }
    return this.typesCache;
  }

  // A new type is a new metadata element, which the activities module reads from the metadata store.
  reloadTypes(metadataChanged = false): void {
    this.typesCache = null;
    if (metadataChanged) {
      this.store.dispatch(new GetMetadata());
    }
  }

  get activityTypes(): SalesInsightsActivityType[] {
    return this.types;
  }

  typeName(type: string | null | undefined): string {
    if (!type) {
      return '';
    }
    const name =
      this.metadataService.getMetadataValue(SALES_INSIGHTS_ACTIVITY_TYPE_TABLE, 'name', type) ||
      this.types.find(known => known.code === type)?.name ||
      type;
    return salesInsightsReadableName(name);
  }

  statusName(status: string | null | undefined): string {
    return this.metadataName(SALES_INSIGHTS_ACTIVITY_STATUS_TABLE, status);
  }

  // Inactive types, such as an archived goal's, are left out unless asked for; activities of them still show.
  typeOptions(includeInactive = false): OptionsList[] {
    const inactive = includeInactive ? new Set<string>() : salesInsightsInactiveTypes(this.types);
    const options = this.options(SALES_INSIGHTS_ACTIVITY_TYPE_TABLE);
    this.types
      .filter(type => !options.some(option => option.id === type.code))
      .forEach(type => options.push({ id: type.code, name: salesInsightsReadableName(type.name || type.code) }));
    return options
      .filter(option => !inactive.has(option.id))
      .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  }

  statusOptions(): OptionsList[] {
    return this.options(SALES_INSIGHTS_ACTIVITY_STATUS_TABLE);
  }

  typeColorIndex(
    type: string | null | undefined,
    overrides?: { [type: string]: number } | null,
    types: SalesInsightsActivityType[] = this.types
  ): number {
    const typeIds = this.options(SALES_INSIGHTS_ACTIVITY_TYPE_TABLE, false).map(option => option.id);
    return salesInsightsTypeColorIndex(type, typeIds, salesInsightsTypeColors(types, overrides));
  }

  typeIcon(
    type: string | null | undefined,
    overrides?: { [type: string]: string } | null,
    types: SalesInsightsActivityType[] = this.types
  ): string {
    const name = type
      ? this.metadataService.getMetadataValue(SALES_INSIGHTS_ACTIVITY_TYPE_TABLE, 'name', type) ||
        types.find(known => known.code === type)?.name
      : '';
    return salesInsightsTypeIcon(type, name, salesInsightsTypeIcons(types, overrides));
  }

  // Reopening sets the status a new activity gets in the create modal, the one with the lowest id.
  reopenStatus(configured?: string | null, doneStatus?: string | null): string | null {
    if (configured) {
      return configured;
    }
    const ids = this.options(SALES_INSIGHTS_ACTIVITY_STATUS_TABLE, false).map(option => option.id);
    return ids.find(id => id !== doneStatus) || null;
  }

  openCustomer(customerId: string): void {
    this.router.navigate(['/customers', GungStringConverterService.toGungString(customerId)]);
  }

  doneStatus(configured?: string | null): string | null {
    if (configured) {
      return configured;
    }
    const statuses: { id?: string; name?: string }[] =
      this.metadataService.getMetadataTableList(SALES_INSIGHTS_ACTIVITY_STATUS_TABLE) || [];
    return statuses.find(status => String(status?.name || '').toUpperCase() === COMPLETED_STATUS_NAME)?.id || null;
  }

  // The list rows are a projection, so every write reads the whole activity first and posts it back with the change,
  // keeping comments, contacts and attachments.
  update(id: string, change: (activity: ActivityInternal) => Partial<ActivityInternal>): Observable<ActivityInternal> {
    return this.activityInternalService
      .getById(id)
      .pipe(
        switchMap(activity => this.activityInternalService.postActivityInternal({ ...activity, ...change(activity) }))
      );
  }

  move(id: string, targetDay: string): Observable<ActivityInternal> {
    return this.update(id, activity => {
      const moved = salesInsightsMovedActivityDates(activity.activityDate, activity.activityEndDate, targetDay);
      return moved
        ? { activityDate: moved.activityDate, activityEndDate: moved.activityEndDate ?? activity.activityEndDate }
        : {};
    });
  }

  setStatus(id: string, status: string): Observable<ActivityInternal> {
    return this.update(id, () => ({ status }));
  }

  bulk(ids: string[], write: (id: string) => Observable<unknown>): Observable<SalesInsightsBulkResult> {
    if (!ids.length) {
      return of({ saved: 0, failed: 0 });
    }
    return from(ids).pipe(
      mergeMap(
        id =>
          write(id).pipe(
            map(() => true),
            catchError(() => of(false))
          ),
        WRITE_CONCURRENCY
      ),
      toArray(),
      map(results => ({ saved: results.filter(ok => ok).length, failed: results.filter(ok => !ok).length }))
    );
  }

  // The view modal saves status changes and comments itself and closes without telling whether it did, so the
  // caller reloads once it is closed.
  openActivity(id: string): Observable<void> {
    return this.activityInternalService.getById(id).pipe(
      switchMap(activity =>
        from(
          this.gungModalService.openViewActivityModal(activity).then(
            () => undefined,
            () => undefined
          )
        )
      )
    );
  }

  // The create modal closes with the saved activity as JSON, and with a plain string when it is cancelled.
  bookActivity(customerId: string, date?: Date): Promise<boolean> {
    return this.gungModalService.openCreateActivityModal(undefined, undefined, customerId, undefined, date).then(
      result => typeof result === 'string' && result.trim().startsWith('{'),
      () => false
    );
  }

  // Books through the standard modal service, so a customer's own create modal is the one that opens; an activity
  // counts towards a goal by its type, so a goal's booking starts on the goal's type. Emits whether anything was saved.
  book(customerId?: string | null, date?: Date | null, activityType?: string | null): Observable<boolean> {
    return from(
      this.openCreateModal(customerId, date, activityType).then(
        result => result,
        () => null
      )
    ).pipe(map(result => salesInsightsSavedActivityIds(result) !== null));
  }

  // The create modal has no input for the type; its initial type is read once its data has loaded, after this.
  protected openCreateModal(
    customerId?: string | null,
    date?: Date | null,
    activityType?: string | null
  ): Promise<unknown> {
    const before = new Set(this.gungModalService.modalRef.values());
    const result = this.gungModalService.openCreateActivityModal(
      undefined,
      undefined,
      customerId || undefined,
      undefined,
      date || undefined
    );
    if (activityType) {
      const instance = [...this.gungModalService.modalRef.values()].find(ref => !before.has(ref))?.componentInstance;
      if (instance && 'initType' in instance) {
        instance.initType = activityType;
      }
    }
    return result;
  }

  private metadataName(table: string, code: string | null | undefined): string {
    if (!code) {
      return '';
    }
    return salesInsightsReadableName(this.metadataService.getMetadataValue(table, 'name', code) || code);
  }

  private options(table: string, sortByName = true): OptionsList[] {
    const records: { id?: string; name?: string }[] = this.metadataService.getMetadataTableList(table) || [];
    const options = records
      .filter(record => !!record?.id)
      .map(record => ({ id: String(record.id), name: salesInsightsReadableName(String(record.name || record.id)) }));
    return sortByName
      ? options.sort((a, b) => a.name.localeCompare(b.name, 'sv'))
      : options.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  }
}
